import { ClipboardList, Image } from 'lucide-react';
import { RowListSkeleton } from '../../../shared/components/RowListSkeleton';
import type { DriverCaptureState } from '../hooks/use-driver-capture';
import { captureEventLabel } from '../policies/capture-step.policy';

const timeOf = (iso: string) => new Date(iso).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });

/** Newest first. A step the phone sent itself has no "Recorded by", so a step is never entered twice for want of seeing it. */
export function DriverCaptureList({ state }: { state: DriverCaptureState }) {
  if (state.status === 'loading') return <RowListSkeleton label="Loading recorded steps" rows={2} />;
  if (state.status === 'error') return <p className="text-sm text-muted">Couldn&apos;t load the recorded steps. Try reloading the page.</p>;
  if (state.entries.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted">
        <ClipboardList className="size-4" aria-hidden /> Nothing recorded for this trip yet.
      </p>
    );
  }
  return (
    <ol aria-label="Recorded for this trip" className="flex flex-col gap-2">
      {[...state.entries].reverse().map((entry) => (
        <li key={entry.id} className="rounded-lg border border-line px-4 py-3 text-sm">
          <p className="font-medium text-fg">
            {captureEventLabel(entry.eventType)} · {timeOf(entry.deviceTime)}
          </p>
          {entry.recordedBy && (
            <p className="text-muted">
              Recorded by {entry.recordedBy.name}
              {entry.driver ? ` for ${entry.driver.name}` : ''}
            </p>
          )}
          {!entry.recordedBy && <p className="text-muted">Sent from the driver&apos;s phone</p>}
          {entry.note && <p className="mt-1 text-fg">{entry.note}</p>}
          {entry.photoCount > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-muted">
              <Image className="size-3.5" aria-hidden /> {entry.photoCount === 1 ? '1 photo' : `${entry.photoCount} photos`}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}
